import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { X, ChevronLeft, ChevronRight, Play, Users, Calendar, Video, CreditCard, MessageSquare, BookOpen, Shield } from 'lucide-react';

const tourSteps = {
  student: [
    {
      title_ar: 'مرحباً بك في Learn With Trust!',
      title_en: 'Welcome to Learn With Trust!',
      description_ar: 'منصة تقنية تربط الطلاب بأساتذة مستقلين في الإمارات. دعنا نأخذك في جولة سريعة لتتعرف على المنصة.',
      description_en: 'A technology platform connecting students with independent tutors in UAE. Let us take you on a quick tour.',
      icon: BookOpen,
      position: 'center'
    },
    {
      title_ar: 'البحث عن أستاذ 🔍',
      title_en: 'Find a Teacher 🔍',
      description_ar: 'استخدم فلاتر البحث المتقدمة للعثور على الأستاذ المثالي حسب المادة، الإمارة، المنطقة، والجنس.',
      description_en: 'Use advanced filters to find the perfect teacher by subject, emirate, area, and gender.',
      icon: Users,
      highlight: '[data-testid="search-teachers"]',
      position: 'bottom'
    },
    {
      title_ar: 'حجز درس 📅',
      title_en: 'Book a Lesson 📅',
      description_ar: 'اختر الأستاذ، حدد الموعد المناسب، واحجز درسك بسهولة. يمكنك اختيار درس أونلاين أو حضوري.',
      description_en: 'Choose a teacher, select a suitable time, and book your lesson easily. Online or in-person.',
      icon: Calendar,
      position: 'center'
    },
    {
      title_ar: 'الغرفة الصفية الافتراضية 💻',
      title_en: 'Virtual Classroom 💻',
      description_ar: 'للدروس الأونلاين، ستجد غرفة صفية متكاملة مع فيديو، صوت، سبورة بيضاء، ومشاركة الشاشة.',
      description_en: 'For online lessons, you\'ll find a full classroom with video, audio, whiteboard, and screen sharing.',
      icon: Video,
      position: 'center'
    },
    {
      title_ar: 'الدفع الآمن 💳',
      title_en: 'Secure Payment 💳',
      description_ar: 'ادفع بأمان عبر بطاقات الائتمان أو Apple Pay. جميع المعاملات محمية ومشفرة.',
      description_en: 'Pay securely via credit cards or Apple Pay. All transactions are protected and encrypted.',
      icon: CreditCard,
      position: 'center'
    }
  ],
  teacher: [
    {
      title_ar: 'مرحباً بك كأستاذ! 🎓',
      title_en: 'Welcome, Teacher! 🎓',
      description_ar: 'أهلاً بك في منصة Trust Ed Techno. ستجد هنا كل ما تحتاجه لإدارة دروسك.',
      description_en: 'Welcome to Trust Ed Techno. Here you\'ll find everything you need to manage your lessons.',
      icon: BookOpen,
      position: 'center'
    },
    {
      title_ar: 'أكمل ملفك الشخصي 📝',
      title_en: 'Complete Your Profile 📝',
      description_ar: 'أضف معلوماتك، المواد التي تدرسها، أسعارك، وجدولك. الملف المكتمل يجذب المزيد من الطلاب.',
      description_en: 'Add your info, subjects, rates, and schedule. A complete profile attracts more students.',
      icon: Users,
      position: 'center'
    },
    {
      title_ar: 'إدارة الحجوزات 📅',
      title_en: 'Manage Bookings 📅',
      description_ar: 'راجع طلبات الحجز، اقبل أو ارفض، وتابع جدولك. ستصلك إشعارات فورية.',
      description_en: 'Review booking requests, accept or reject, and track your schedule. You\'ll get instant notifications.',
      icon: Calendar,
      position: 'center'
    },
    {
      title_ar: 'الغرفة الصفية 💻',
      title_en: 'Virtual Classroom 💻',
      description_ar: 'أنشئ غرفة صفية للدروس الأونلاين. استخدم السبورة البيضاء ومشاركة الشاشة للشرح.',
      description_en: 'Create a classroom for online lessons. Use the whiteboard and screen sharing for explanations.',
      icon: Video,
      position: 'center'
    },
    {
      title_ar: 'تتبع أرباحك 💰',
      title_en: 'Track Your Earnings 💰',
      description_ar: 'شاهد أرباحك، العمولات، والتحويلات. كل شيء شفاف وواضح.',
      description_en: 'View your earnings, commissions, and transfers. Everything is transparent and clear.',
      icon: CreditCard,
      position: 'center'
    }
  ],
  parent: [
    {
      title_ar: 'مرحباً بك ولي الأمر! 👨‍👩‍👧',
      title_en: 'Welcome, Parent! 👨‍👩‍👧',
      description_ar: 'تابع تعليم أبنائك بسهولة من خلال لوحة التحكم الخاصة بك.',
      description_en: 'Track your children\'s education easily through your dashboard.',
      icon: Shield,
      position: 'center'
    },
    {
      title_ar: 'إضافة الأبناء 👦👧',
      title_en: 'Add Children 👦👧',
      description_ar: 'أضف أبناءك وسيتم إنشاء حساب طالب لكل منهم تلقائياً. يمكنك إدارة حساباتهم من هنا.',
      description_en: 'Add your children and a student account will be created for each. You can manage their accounts here.',
      icon: Users,
      position: 'center'
    },
    {
      title_ar: 'متابعة الحجوزات 📅',
      title_en: 'Track Bookings 📅',
      description_ar: 'شاهد جميع حجوزات أبنائك، الدروس القادمة، والمكتملة.',
      description_en: 'View all your children\'s bookings, upcoming and completed lessons.',
      icon: Calendar,
      position: 'center'
    },
    {
      title_ar: 'مراقبة الجلسات 👁️',
      title_en: 'Monitor Sessions 👁️',
      description_ar: 'راقب جلسات أبنائك الأونلاين في أي وقت. ستصلك إشعارات عند بدء وانتهاء كل جلسة.',
      description_en: 'Monitor your children\'s online sessions anytime. You\'ll get notifications when sessions start and end.',
      icon: Video,
      position: 'center'
    },
    {
      title_ar: 'التقارير والمدفوعات 📊',
      title_en: 'Reports & Payments 📊',
      description_ar: 'احصل على تقارير أسبوعية عن تقدم أبنائك وإدارة المدفوعات بسهولة.',
      description_en: 'Get weekly reports on your children\'s progress and manage payments easily.',
      icon: CreditCard,
      position: 'center'
    }
  ]
};

const InteractiveTour = ({ onComplete }) => {
  const { user } = useAuth();
  const { language, localize } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const steps = tourSteps[user?.role] || tourSteps.student;
  const step = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('tourCompleted', 'true');
    setIsVisible(false);
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.setItem('tourCompleted', 'true');
    setIsVisible(false);
    onComplete?.();
  };

  if (!isVisible) return null;

  const Icon = step.icon;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleSkip} />
      
      {/* Tour Card */}
      <div className={`relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden ${language === 'ar' ? 'rtl' : 'ltr'}`} dir={language === 'ar' ? 'rtl' : 'ltr'}>
        {/* Progress Bar */}
        <div className="h-1 bg-slate-200">
          <div 
            className="h-full bg-emerald-500 transition-all duration-300"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>

        {/* Close Button */}
        <button 
          onClick={handleSkip}
          className="absolute top-4 left-4 p-1 rounded-full hover:bg-slate-100 transition-colors"
        >
          <X className="w-5 h-5 text-slate-400" />
        </button>

        {/* Content */}
        <div className="p-6 pt-12">
          {/* Icon */}
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-100 flex items-center justify-center">
            <Icon className="w-8 h-8 text-emerald-600" />
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-slate-900 text-center mb-2">
            {localize({ ar: step.title_ar, en: step.title_en })}
          </h2>

          {/* Description */}
          <p className="text-slate-600 text-center mb-6 leading-relaxed">
            {localize({ ar: step.description_ar, en: step.description_en })}
          </p>

          {/* Step Counter */}
          <div className="flex justify-center gap-1 mb-6">
            {steps.map((_, idx) => (
              <div 
                key={idx}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentStep ? 'bg-emerald-500' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex gap-3">
            {currentStep > 0 && (
              <Button 
                variant="outline" 
                onClick={handlePrev}
                className="flex-1"
              >
                <ChevronRight className="w-4 h-4 ml-1" />
                {localize({ ar: 'السابق', en: 'Previous' })}
              </Button>
            )}
            
            <Button 
              onClick={handleNext}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            >
              {currentStep === steps.length - 1 
                ? (localize({ ar: 'ابدأ الآن!', en: 'Start Now!' }))
                : (localize({ ar: 'التالي', en: 'Next' }))
              }
              {currentStep < steps.length - 1 && <ChevronLeft className="w-4 h-4 mr-1" />}
            </Button>
          </div>

          {/* Skip Link */}
          <button 
            onClick={handleSkip}
            className="w-full mt-3 text-sm text-slate-400 hover:text-slate-600 transition-colors"
          >
            {localize({ ar: 'تخطي الجولة', en: 'Skip tour' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InteractiveTour;
